/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { Switch, Route, useHistory } from 'react-router-dom';
import Websocket from '../Websocket';
import { LineStatusViewerHud } from '../LineStatusViewer';
// import HomePage from '../HomePage/index';
// import NotFoundPage from 'containers/NotFoundPage/Loadable';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
// import makeSelectLogin from '../Login/selectors';
import GlobalStyle from '../../global-styles';
import '../../scss/style.scss';
import { v4 as uuidv4 } from 'uuid';

import ProjectList from '../../../config/containers/App/projectList';
import { makeSelectConfig, makeSelectArchiveMode, makeSelectArchiveGrayscale, makeSelectLoginState, makeSelectKeyboardState, makeSelectAppHash, makeSelectDebugMode } from './selectors';
import { setProjectConfig, setAuthState, setSettings } from './actions';
import { Wrapper } from './AppWrapper';
import auth from '../../../config/utils/auth';
import { useInjectSaga } from '../../../config/utils/injectSaga';
import { useInjectReducer } from '../../../config/utils/injectReducer';
// import makeSelectLogin from './selectors';
import reducer from './reducer';
import saga from './saga';

/*  LOGIN ACCES */
import UserContext from '../../../config/utils/hooks/userContext';
import { setupLogin } from '../../../config/utils/permissions_roles/loginService';

/* KEYBOARD */
import KeyboardModal from '../../components/KeyboardModal';
import _ from 'lodash';
import { Snackbar } from '@mui/material';
import ArchiveSlider from '../Sidebar/ArchiveSlider';
import Indicators from '../Sidebar/Indicators';
import makeSelectModalManager from '../ModalManager/selectors';
import ReactDOM from 'react-dom';
import makeSelectSiderbar from '../Sidebar/selectors';
import { UnitSystemProvider } from '../../../config/utils/hooks/unitSystemContext';
import { RefreshDatesProvider } from '../Websocket/RefreshDatesProvider';
import StatisticsOverlay from '../Fiber/3dObjects/productionFlow/statistics/StatisticsOverlay';
import { ControlFeedbackProvider } from '../../components/ControlFeedbackSnackbar/ControlFeedbackContext';
// import ModalManager from '../ModalManager';

const HomePage = React.lazy(() => import(/* webpackChunkName: "HomePage" */ '../HomePage/index'));
const ModalManager = React.lazy(() => import(/* webpackChunkName: "ModalManager" */ '../ModalManager'));
// eslint-disable-next-line no-unused-vars
const Insights = React.lazy(() => import('../Insights/index'));

function ArchiveModeSnackbar(props) {
  const snackbarRoot = document.getElementById('snackbarOverlay');
  return ReactDOM.createPortal(
    <Snackbar open={true} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} style={{ zIndex: 9999, position: 'absolute', top: '0', left: '50%', transform: 'translateX(-50%)' }}>
      <Indicators>
        <ArchiveSlider dispatch={props.dispatch} archive={props.archive} intervalStep={props.intervalStep} />
      </Indicators>
    </Snackbar>,
    snackbarRoot
  );
}

export function App(props) {
  useInjectReducer({ key: 'app', reducer });
  useInjectSaga({ key: 'app', saga });

  const dispatch = useDispatch();
  const history = useHistory();
  // console.log('APP: ', props);
  /* Load App based on environment variable config settings */
  const project_name = process.env.REACT_APP_PROJECT_NAME;
  const projectList = ProjectList;
  const project_configuration = projectList[project_name];
  const default_unit_system = _.get(project_configuration, 'defaultUnitSystem', 'metric');

  useEffect(() => {
    let sessionId = sessionStorage.getItem('session_uuid');
    if (!sessionId) {
      sessionId = uuidv4();
      sessionStorage.setItem('session_uuid', sessionId);
    }
  }, []);

  // Fix CoreUI CSidebar keydown handler that crashes when e.key is undefined
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === undefined) {
        Object.defineProperty(e, 'key', { value: '', writable: true, configurable: true });
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  /* Update global state through global reducer with appropriate project configuration */
  useEffect(() => {
    if (project_configuration) {
      dispatch(setProjectConfig(project_configuration));
    }
  }, [project_configuration]);

  const localStorage = global.window.localStorage;
  const [loginData, setLoginData] = useState(null);
  const [privilege, setPrivilege] = useState(-1);

  /* TEST LOGIN ACCES */
  const initiateLogin = (role_key) => {
    const { permissions, user } = setupLogin(role_key);
    setPrivilege(role_key);
    setCurrentUser({
      permissions,
      user,
      ...currentUser
    });
  };

  useEffect(() => {
    let interval = null;

    if (auth.loggedIn()) {
      const time = 1000 * 5;
      interval = setInterval(() => {
        if (auth.loggedIn()) {
          auth.refreshToken();
        }
      }, time);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
  }, [props.loginState.loggedIn]);

  useEffect(() => {
    const storedHash = localStorage.getItem('appHash');

    if (!storedHash) {
      localStorage.setItem('appHash', props.appHash);
    } else if (storedHash !== props.appHash) {
      console.log('App version has changed. Clearing cache and refreshing...');
      localStorage.setItem('appHash', props.appHash);

      window.location.reload(true);
    }
  }, [props.appHash]);

  // console.log('# ---- props.loginState : ', props.loginState, ' ----------# ');
  // console.log('# ---- # loginData : ', loginData, ' #----------# ');

  const [currentUser, setCurrentUser] = useState(0);
  const value = useMemo(() => ({ currentUser, setCurrentUser }), [currentUser]);
  /* */

  useEffect(() => {
    auth.reload(localStorage.getItem('token'), setLoginData, initiateLogin, setAuthState, setCurrentUser, currentUser);
  }, []);

  useEffect(() => {
    dispatch(setSettings(_.get(currentUser, 'settings', [])));
  }, [currentUser]);

  useEffect(() => {}, [privilege]);

  useEffect(() => {
    if (props.loginState.loggedIn) {
      setLoginData(props.loginState.user);
      const defaultPage = '/dashboard';
      history.push(defaultPage);
    } else {
      const token = localStorage.getItem('token');
      if (token != null) auth.reload(token, setLoginData, initiateLogin, setAuthState, setCurrentUser, currentUser);
    }
  }, [props.loginState.loggedIn]);

  useEffect(() => {
    initiateLogin(props?.loginState?.privilege);
  }, [props.loginState]);

  useEffect(() => {
    if (loginData != null) {
      dispatch(setAuthState(true, { username: loginData.username, privilege: loginData.privilege, vnc: loginData.vnc }));
      initiateLogin(loginData?.privilege);
    } else {
      dispatch(setAuthState(false));
      initiateLogin(-1);
    }
  }, [loginData]);

  const isAnyModalOpen = props.modalmanager?.modal?.length > 0;

  return (
    <UserContext.Provider value={value}>
      <ControlFeedbackProvider>
        <UnitSystemProvider default_unit_system={default_unit_system}>
          <RefreshDatesProvider>
            <Wrapper className={`${'app--shell'}${props.archiveMode && props.archiveGrayscale ? ' archive-grayscale' : ''}${props.debugMode ? ' debug' : ''}`}>
              <Helmet titleTemplate="%s - XLAM Manager" defaultTitle={project_name}>
                <meta name="description" content="XLAM Manager application" />
              </Helmet>
              <Websocket archiveMode={props.archiveMode} debugMode={props.debugMode} />
              <Switch>
                <Route path="/" name="Homepage" render={(props) => <HomePage {...props} />} />
              </Switch>
              <ModalManager archiveMode={props.archiveMode} debugMode={props.debugMode} />
              {props.debugMode && <LineStatusViewerHud />}
              <StatisticsOverlay />
              <KeyboardModal />
              <GlobalStyle />
              {isAnyModalOpen && props.archiveMode && <ArchiveModeSnackbar dispatch={dispatch} archive={props.archiveMode} intervalStep={props.sidebar.archiveIntervalStep} />}
            </Wrapper>
          </RefreshDatesProvider>
        </UnitSystemProvider>
      </ControlFeedbackProvider>
    </UserContext.Provider>
  );
}

App.propTypes = {
  children: PropTypes.node,
  loggedIn: PropTypes.object,
  archiveMode: PropTypes.bool,
  debugMode: PropTypes.bool
};

const mapStateToProps = createStructuredSelector({
  loginState: makeSelectLoginState(),
  configuration: makeSelectConfig(),
  archiveMode: makeSelectArchiveMode(),
  archiveGrayscale: makeSelectArchiveGrayscale(),
  debugMode: makeSelectDebugMode(),
  keyboardState: makeSelectKeyboardState(),
  modalmanager: makeSelectModalManager(),
  sidebar: makeSelectSiderbar(),
  appHash: makeSelectAppHash()
});

export default connect(mapStateToProps)(App);
